import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaEye, FaTrash } from 'react-icons/fa';
import Wrapper from '../../components/Wrapper';
import { listCrops, createCrop, getStates, deleteCrop } from '../../services/recommendationsApi'; 
import { Spinner } from 'react-bootstrap';

const RecommendationCrops = () => {
  const navigate = useNavigate();
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statesList, setStatesList] = useState([]);
  const [selectedState, setSelectedState] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const statesData = await getStates();
        setStatesList(statesData);
      } catch (err) {
        console.error("Failed to fetch states", err);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (q = debouncedSearch, stateId = selectedState) => {
    const id = ++requestId.current;
    setLoading(true);
    setError('');

    try {
      const res = await listCrops({ search: q, state_id: stateId });

      if (id !== requestId.current) return;
      setCrops(res.crops || []);
    } catch (e) {
      if (id !== requestId.current) return;
      setError(e.response?.data?.message || e.message || 'Failed to load crops');
      setCrops([]);
    } finally {
      if (id === requestId.current) {
        setLoading(false);
      }
    }
  }, [debouncedSearch, selectedState]);

  useEffect(() => {
    load(debouncedSearch, selectedState);
  }, [load, debouncedSearch, selectedState]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newSlug.trim()) return;
    setSubmitting(true);
    try {
      await createCrop({ slug: newSlug.trim() });
      setShowAdd(false);
      setNewSlug('');
      setSearch('');
      if (debouncedSearch) {
        setDebouncedSearch('');
      } else {
        load('');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to create crop');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Are you sure you want to delete the crop "${slug}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setLoading(true);
      await deleteCrop(slug);
      await load(debouncedSearch, selectedState);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to delete crop');
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <div className="py-4">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
          <h1 className="fw-bolder">Crop Recommendation</h1>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <button className="btn btn-dark" onClick={() => setShowAdd(true)}>
              <FaPlus /> Add crop
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {showAdd && (
          <div className="card mb-3 p-3">
            <form onSubmit={handleCreate} className="d-flex flex-wrap gap-2 align-items-end">
              <div className="flex-grow-1" style={{ minWidth: '200px' }}>
                <label className="form-label small mb-0">Crop slug (e.g. wheat, organic methi)</label>
                <input
                  type="text"
                  className="form-control"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value)}
                  placeholder="wheat"
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setShowAdd(false);
                  setNewSlug('');
                }}
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        <div className="d-flex flex-wrap gap-3 mb-3">
          <div className="input-group" style={{ maxWidth: 320 }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search by crop name or slug..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <div style={{ maxWidth: 250, minWidth: 200 }}>
            <select
              className="form-select"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <option value="">All States</option>
              {statesList.map(state => (
                <option key={state.id} value={state.id}>
                  {state.name_en}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        ) : crops.length === 0 ? (
          <p className="text-muted">
            {debouncedSearch
              ? `No crops found for "${debouncedSearch}".`
              : 'No crops yet. Add a crop to manage stages and prescriptions.'}
          </p>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Slug</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {crops.map((c) => (
                  <tr key={c.id}>
                    <td className="align-middle">{c.name_en}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => navigate(`/recommendations/${encodeURIComponent(c.slug)}/view`)}
                      >
                        <FaEye /> View
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => navigate(`/recommendations/${encodeURIComponent(c.slug)}`)}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(c.slug)}
                      >
                        <FaTrash /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default RecommendationCrops;